import {useInfiniteQuery, useMutation, useQueryClient} from '@tanstack/react-query';
import {blockUser, fetchBlockedUsers, unblockUser} from '../endpoints/api';
import useAuth from './useAuth';

// Query key prefixes for chat comment lists across all chat types (place,
// event, lifestyle, community). When a user is blocked we optimistically
// remove their comments from these caches and then invalidate so the server
// can re-filter on the next fetch.
const CHAT_COMMENT_KEY_PREFIXES = [
  'infinite_place_comments',
  'infinite_featured_place_comments',
  'place_comments',
  'place_replies',
  'infinite_event_comments',
  'infinite_featured_event_comments',
  'event_comments',
  'event_replies',
  'infinite_lifestyle_comments',
  'infinite_featured_lifestyle_comments',
  'lifestyle_comments',
  'lifestyle_replies',
  'infinite_community_comments',
  'infinite_featured_community_comments',
  'community_comments',
  'community_replies',
];

const isChatCommentKey = (key: unknown): boolean => {
  if (!Array.isArray(key) || typeof key[0] !== 'string') return false;
  return CHAT_COMMENT_KEY_PREFIXES.includes(key[0]);
};

/**
 * Remove comments authored by the blocked user from a cached query payload.
 * Supports both infinite query shape ({pages: [{data: {data: [...]}}]}) and
 * the replies/mentions shape ({replies: [...], mentions: [...]}).
 */
const pruneBlockedFromCache = (data: any, blockedUserId: number): any => {
  if (!data) return data;

  // Infinite query
  if (Array.isArray(data.pages)) {
    return {
      ...data,
      pages: data.pages.map((page: any) => {
        const inner = page?.data?.data;
        if (!Array.isArray(inner)) return page;
        return {
          ...page,
          data: {
            ...page.data,
            data: inner.filter((c: any) => c?.user_id !== blockedUserId),
          },
        };
      }),
    };
  }

  // Replies / mentions shape
  if (Array.isArray((data as any).replies) || Array.isArray((data as any).mentions)) {
    return {
      ...data,
      replies: Array.isArray(data.replies)
        ? data.replies.filter((c: any) => c?.user_id !== blockedUserId)
        : data.replies,
      mentions: Array.isArray(data.mentions)
        ? data.mentions.filter((c: any) => c?.user_id !== blockedUserId)
        : data.mentions,
    };
  }

  return data;
};

/**
 * Mutation hook for blocking a user. Invalidates caches that depend on
 * connection state so the UI reflects the block immediately without a manual
 * page refresh.
 */
export const useBlockUser = (profileId?: string | number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: number) => blockUser(userId),
    onMutate: async (userId: number) => {
      // Cancel any in-flight chat comment fetches so they don't overwrite our
      // optimistic prune.
      await queryClient.cancelQueries({predicate: (q) => isChatCommentKey(q.queryKey)});

      // Snapshot existing chat caches for rollback.
      const snapshots = queryClient.getQueriesData({
        predicate: (q) => isChatCommentKey(q.queryKey),
      });

      // Optimistically remove the blocked user's comments.
      queryClient.setQueriesData(
        {predicate: (q) => isChatCommentKey(q.queryKey)},
        (old: any) => pruneBlockedFromCache(old, userId)
      );

      return {snapshots};
    },
    onError: (_err, _userId, context: any) => {
      if (context?.snapshots) {
        for (const [key, value] of context.snapshots) {
          queryClient.setQueryData(key, value);
        }
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries(['publicProfile']);
      queryClient.invalidateQueries(['connections']);
      queryClient.invalidateQueries(['friendRequests']);
      if (profileId !== undefined && profileId !== null) {
        queryClient.invalidateQueries(['publicProfile', String(profileId)]);
      }
      // Refetch chat comment lists so the server can re-filter.
      queryClient.invalidateQueries({predicate: (q) => isChatCommentKey(q.queryKey)});
    },
  });
};

export const useUnblockUser = (profileId?: string | number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: number) => unblockUser(userId),
    onMutate: async (userId: number) => {
      await queryClient.cancelQueries({queryKey: ['blocked_users']});
      const snapshots = queryClient.getQueriesData({queryKey: ['blocked_users']});
      // Optimistically remove the unblocked user from the blocked-users list.
      queryClient.setQueriesData({queryKey: ['blocked_users']}, (old: any) => {
        if (!old || !Array.isArray(old.pages)) return old;
        return {
          ...old,
          pages: old.pages.map((page: any) => {
            const inner = page?.data?.data;
            if (!Array.isArray(inner)) return page;
            return {
              ...page,
              data: {
                ...page.data,
                data: inner.filter((u: any) => u?.id !== userId),
              },
            };
          }),
        };
      });
      return {snapshots};
    },
    onError: (_err, _userId, context: any) => {
      if (context?.snapshots) {
        for (const [key, value] of context.snapshots) {
          queryClient.setQueryData(key, value);
        }
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries(['publicProfile']);
      queryClient.invalidateQueries(['connections']);
      queryClient.invalidateQueries(['blocked_users']);
      queryClient.invalidateQueries(['friendRequests']);
      if (profileId !== undefined && profileId !== null) {
        queryClient.invalidateQueries(['publicProfile', String(profileId)]);
      }
      // Refetch chat comment lists so previously hidden content from the
      // unblocked user is reloaded.
      queryClient.invalidateQueries({predicate: (q) => isChatCommentKey(q.queryKey)});
    },
  });
};

export interface BlockedUser {
  id: number;
  name: string;
  surname?: string;
  handle?: string;
  profile_photo_url?: string;
  neighborhood?: string | null;
}

export const useBlockedUsers = () => {
  const {user} = useAuth();
  const userId = user?.id;

  const query = useInfiniteQuery(
    ['blocked_users', userId],
    async ({pageParam = 1}) => {
      if (!userId) return null;
      return fetchBlockedUsers(userId, {page: pageParam});
    },
    {
      enabled: !!userId,
      getNextPageParam: (lastPage: any) => {
        const meta = lastPage?.data?.meta ?? lastPage?.data;
        const current = meta?.current_page;
        const last = meta?.last_page;
        if (typeof current === 'number' && typeof last === 'number' && current < last) {
          return current + 1;
        }
        return undefined;
      },
    },
  );

  const blockedUsers: BlockedUser[] =
    query.data?.pages.flatMap((page: any) => page?.data?.data ?? []) ?? [];

  // `isLoading` is false while the query is disabled (waiting for userId).
  // Fall back to a flag that stays true until we have at least one page.
  const isInitialLoading =
    query.isLoading ||
    (!!userId && !query.data && query.fetchStatus !== 'idle') ||
    (!userId);

  return {...query, blockedUsers, isInitialLoading};
};
